import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Receipt, SearchIcon, CheckCircle2Icon, TruckIcon, UserIcon, CalendarIcon, HashIcon, XIcon, IndianRupee, BanknoteIcon, QrCodeIcon, TicketIcon, PrinterIcon } from 'lucide-react';
import { PaymentModal } from '../components/PaymentModal';
import { BillReceipt } from '../components/BillReceipt';
interface VehicleEntry {
  gatePassNo: string;
  vehicleNo: string;
  vehicleType: string;
  user: string;
  dateTime: string;
}
const vehicleRecords: VehicleEntry[] = [{
  gatePassNo: 'GP-2026-0001',
  vehicleNo: 'MH-12-AB-1234',
  vehicleType: 'Truck',
  user: 'Rajesh Kumar',
  dateTime: '2026-04-15 08:30 AM'
}, {
  gatePassNo: 'GP-2026-0002',
  vehicleNo: 'MH-14-CD-5678',
  vehicleType: 'Car',
  user: 'Priya Sharma',
  dateTime: '2026-04-15 09:15 AM'
}, {
  gatePassNo: 'GP-2026-0003',
  vehicleNo: 'GJ-05-EF-9012',
  vehicleType: 'Van',
  user: 'Amit Patel',
  dateTime: '2026-04-15 09:45 AM'
}, {
  gatePassNo: 'GP-2026-0004',
  vehicleNo: 'DL-01-GH-3456',
  vehicleType: 'Trailer',
  user: 'Sunil Verma',
  dateTime: '2026-04-15 10:00 AM'
}, {
  gatePassNo: 'GP-2026-0005',
  vehicleNo: 'RJ-14-IJ-7890',
  vehicleType: 'Truck',
  user: 'Deepak Singh',
  dateTime: '2026-04-15 10:30 AM'
}, {
  gatePassNo: 'GP-2026-0006',
  vehicleNo: 'KA-01-KL-2345',
  vehicleType: 'Car',
  user: 'Meena Reddy',
  dateTime: '2026-04-15 11:00 AM'
}, {
  gatePassNo: 'GP-2026-0007',
  vehicleNo: 'TN-09-MN-6789',
  vehicleType: 'Bike',
  user: 'Karthik Nair',
  dateTime: '2026-04-15 11:20 AM'
}, {
  gatePassNo: 'GP-2026-0008',
  vehicleNo: 'UP-32-OP-0123',
  vehicleType: 'Truck',
  user: 'Vikram Yadav',
  dateTime: '2026-04-15 11:45 AM'
}, {
  gatePassNo: 'GP-2026-0009',
  vehicleNo: 'MP-09-QR-4567',
  vehicleType: 'Van',
  user: 'Anita Gupta',
  dateTime: '2026-04-15 12:10 PM'
}, {
  gatePassNo: 'GP-2026-0010',
  vehicleNo: 'HR-26-ST-8901',
  vehicleType: 'Trailer',
  user: 'Rohit Malik',
  dateTime: '2026-04-15 12:30 PM'
}];
const vehicleTypeBadge: Record<string, {
  bg: string;
  text: string;
}> = {
  Truck: {
    bg: 'bg-amber-50 border-amber-200',
    text: 'text-amber-700'
  },
  Car: {
    bg: 'bg-blue-50 border-blue-200',
    text: 'text-blue-700'
  },
  Bike: {
    bg: 'bg-green-50 border-green-200',
    text: 'text-green-700'
  },
  Van: {
    bg: 'bg-purple-50 border-purple-200',
    text: 'text-purple-700'
  },
  Trailer: {
    bg: 'bg-rose-50 border-rose-200',
    text: 'text-rose-700'
  }
};
const feeStructure: Record<string, {
  parking: number;
  entry: number;
}> = {
  Truck: {
    parking: 200,
    entry: 150
  },
  Car: {
    parking: 100,
    entry: 50
  },
  Bike: {
    parking: 50,
    entry: 20
  },
  Van: {
    parking: 150,
    entry: 100
  },
  Trailer: {
    parking: 250,
    entry: 200
  }
};
type PaymentMethod = 'Cash' | 'QR' | 'Coupon';
export function CreateBillingPage({
  userName
}: {
  userName: string;
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResult, setSearchResult] = useState<VehicleEntry | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [parkingFee, setParkingFee] = useState<number>(0);
  const [entryFee, setEntryFee] = useState<number>(0);
  const [otherCharges, setOtherCharges] = useState<number>(0);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('Cash');
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState<'QR' | 'Coupon' | null>(null);
  const [billGenerated, setBillGenerated] = useState(false);
  const [billNumber, setBillNumber] = useState('');
  const [showReceipt, setShowReceipt] = useState(false);
  const total = parkingFee + entryFee + otherCharges;
  useEffect(() => {
    if (searchResult) {
      const fees = feeStructure[searchResult.vehicleType] || {
        parking: 0,
        entry: 0
      };
      setParkingFee(fees.parking);
      setEntryFee(fees.entry);
    }
  }, [searchResult]);
  const handleSearch = () => {
    if (!searchQuery.trim()) return;
    const q = searchQuery.trim().toUpperCase();
    const found = vehicleRecords.find(r => r.gatePassNo.toUpperCase() === q || r.vehicleNo.toUpperCase() === q);
    setSearchResult(found || null);
    setNotFound(!found);
    setBillGenerated(false);
    setHasSearched(true);
    setOtherCharges(0);
    setPaymentMethod('Cash');
    setPaymentConfirmed(false);
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };
  const handleSelectPayment = (method: PaymentMethod) => {
    setPaymentMethod(method);
    setPaymentConfirmed(method === 'Cash');
    if (method === 'QR' || method === 'Coupon') {
      setShowPaymentModal(method);
    }
  };
  const handlePaymentConfirmed = () => {
    setPaymentConfirmed(true);
    setShowPaymentModal(null);
  };
  const handleGenerateBill = () => {
    setBillNumber(`INV-2026-${Math.floor(1000 + Math.random() * 9000)}`);
    setBillGenerated(true);
  };
  const handleReset = () => {
    setSearchQuery('');
    setSearchResult(null);
    setNotFound(false);
    setBillGenerated(false);
    setHasSearched(false);
    setPaymentConfirmed(false);
    setShowPaymentModal(null);
    setShowReceipt(false);
  };
  return <div className="w-full max-w-4xl mx-auto" data-id="element-527">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5
    }} className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-100 p-5 xs:p-6 sm:p-8" data-id="element-528">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6" data-id="element-529">
          <div className="bg-brand-teal/10 p-2 rounded-xl border border-brand-teal/20" data-id="element-530">
            <Receipt className="w-6 h-6 text-brand-teal" data-id="element-531" />
          </div>
          <div data-id="element-532">
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight" data-id="element-533">
              Create Billing
            </h2>
            <p className="text-sm text-slate-500" data-id="element-534">
              Search by gate pass and generate invoice for vehicle.
            </p>
          </div>
        </div>

        {/* Search Bar */}
        <div className="flex gap-3 mb-6" data-id="element-535">
          <div className="relative flex-1" data-id="element-536">
            <SearchIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-slate-400 pointer-events-none" data-id="element-537" />
            <input type="text" placeholder="Enter Gate Pass No. or Vehicle No. (e.g. GP-2026-0001)" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} onKeyDown={handleKeyDown} className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-teal/30 focus:border-brand-teal transition-all" data-id="element-538" />
          </div>
          <button onClick={handleSearch} className="px-5 py-3 bg-brand-teal text-white rounded-xl font-medium text-sm hover:bg-brand-tealHover transition-colors whitespace-nowrap" data-id="element-539">
            Search
          </button>
        </div>

        {/* Not Found State */}
        <AnimatePresence mode="wait" data-id="element-540">
          {hasSearched && notFound && <motion.div key="not-found" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} className="border border-red-200 bg-red-50 rounded-xl p-6 text-center" data-id="element-541">
              <div className="text-red-400 mb-2" data-id="element-542">
                <XIcon className="w-10 h-10 mx-auto" data-id="element-543" />
              </div>
              <p className="text-red-700 font-semibold text-sm" data-id="element-544">
                No record found
              </p>
              <p className="text-red-500 text-xs mt-1" data-id="element-545">
                Please check the gate pass number or vehicle number and try
                again.
              </p>
            </motion.div>}

          {/* Search Result & Billing Form */}
          {searchResult && !billGenerated && <motion.div key="result" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} className="border border-slate-200 rounded-xl overflow-hidden" data-id="element-546">
              <div className="bg-slate-50 px-5 py-3 border-b border-slate-200" data-id="element-547">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-548">
                  Vehicle Details
                </p>
              </div>
              <div className="p-5" data-id="element-549">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6" data-id="element-550">
                  <div className="flex items-start gap-3" data-id="element-551">
                    <div className="bg-brand-teal/10 p-2 rounded-lg" data-id="element-552">
                      <HashIcon className="w-4 h-4 text-brand-teal" data-id="element-553" />
                    </div>
                    <div data-id="element-554">
                      <p className="text-xs text-slate-400 font-medium" data-id="element-555">
                        Gate Pass No.
                      </p>
                      <p className="text-sm font-bold text-slate-800 font-mono" data-id="element-556">
                        {searchResult.gatePassNo}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-557">
                    <div className="bg-blue-50 p-2 rounded-lg" data-id="element-558">
                      <TruckIcon className="w-4 h-4 text-blue-600" data-id="element-559" />
                    </div>
                    <div data-id="element-560">
                      <p className="text-xs text-slate-400 font-medium" data-id="element-561">
                        Vehicle No.
                      </p>
                      <p className="text-sm font-bold text-slate-800 font-mono" data-id="element-562">
                        {searchResult.vehicleNo}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-563">
                    <div className="bg-amber-50 p-2 rounded-lg" data-id="element-564">
                      <TruckIcon className="w-4 h-4 text-amber-600" data-id="element-565" />
                    </div>
                    <div data-id="element-566">
                      <p className="text-xs text-slate-400 font-medium" data-id="element-567">
                        Vehicle Type
                      </p>
                      {(() => {
                    const badge = vehicleTypeBadge[searchResult.vehicleType];
                    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-lg text-xs font-medium border mt-0.5 ${badge?.bg || ''} ${badge?.text || ''}`} data-id="element-568">
                            {searchResult.vehicleType}
                          </span>;
                  })()}
                    </div>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-569">
                    <div className="bg-green-50 p-2 rounded-lg" data-id="element-570">
                      <CalendarIcon className="w-4 h-4 text-green-600" data-id="element-571" />
                    </div>
                    <div data-id="element-572">
                      <p className="text-xs text-slate-400 font-medium" data-id="element-573">
                        Entry Date & Time
                      </p>
                      <p className="text-sm text-slate-700" data-id="element-574">
                        {searchResult.dateTime}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="border-t border-slate-200 pt-6" data-id="element-575">
                  <h3 className="text-sm font-bold text-slate-800 mb-4" data-id="element-576">
                    Billing Details
                  </h3>
                  <div className="space-y-4" data-id="element-577">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-578">
                      <div data-id="element-579">
                        <label className="block text-xs font-semibold text-slate-500 mb-1.5" data-id="element-580">
                          Parking Fee (₹)
                        </label>
                        <div className="relative" data-id="element-581">
                          <IndianRupee className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-582" />
                          <input type="number" value={parkingFee} disabled className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm text-slate-400 cursor-not-allowed" data-id="element-583" />
                        </div>
                      </div>
                      <div data-id="element-584">
                        <label className="block text-xs font-semibold text-slate-500 mb-1.5" data-id="element-585">
                          Entry Fee (₹)
                        </label>
                        <div className="relative" data-id="element-586">
                          <IndianRupee className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-587" />
                          <input type="number" value={entryFee} disabled className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm text-slate-400 cursor-not-allowed" data-id="element-588" />
                        </div>
                      </div>
                    </div>
                    <div data-id="element-589">
                      <label className="block text-xs font-semibold text-slate-500 mb-1.5" data-id="element-590">
                        Other Charges (₹)
                      </label>
                      <div className="relative" data-id="element-591">
                        <IndianRupee className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-592" />
                        <input type="number" min="0" value={otherCharges || ''} onChange={e => setOtherCharges(Number(e.target.value))} className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-teal/30 focus:border-brand-teal transition-all" placeholder="0.00" data-id="element-593" />
                      </div>
                    </div>

                    <div className="pt-2" data-id="element-594">
                      <label className="block text-xs font-semibold text-slate-500 mb-2" data-id="element-595">
                        Payment Method
                      </label>
                      <div className="flex gap-2" data-id="element-596">
                        <button onClick={() => handleSelectPayment('Cash')} className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium transition-colors border ${paymentMethod === 'Cash' && paymentConfirmed ? 'bg-brand-teal text-white border-brand-teal' : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'}`} data-id="element-597">
                          <BanknoteIcon className="w-4 h-4" data-id="element-598" />
                          Cash
                        </button>
                        <button onClick={() => handleSelectPayment('QR')} className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium transition-colors border ${paymentMethod === 'QR' && paymentConfirmed ? 'bg-brand-teal text-white border-brand-teal' : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'}`} data-id="element-599">
                          <QrCodeIcon className="w-4 h-4" data-id="element-600" />
                          QR Code
                        </button>
                        <button onClick={() => handleSelectPayment('Coupon')} className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium transition-colors border ${paymentMethod === 'Coupon' && paymentConfirmed ? 'bg-brand-teal text-white border-brand-teal' : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'}`} data-id="element-601">
                          <TicketIcon className="w-4 h-4" data-id="element-602" />
                          Coupon
                        </button>
                      </div>
                      {paymentConfirmed && paymentMethod !== 'Cash' && <motion.p initial={{
                    opacity: 0,
                    y: -5
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} className="text-xs text-green-600 font-medium mt-2 flex items-center gap-1" data-id="element-603">
                          <CheckCircle2Icon className="w-3.5 h-3.5" data-id="element-604" />
                          {paymentMethod === 'QR' ? 'QR payment confirmed' : 'Coupon applied successfully'}
                        </motion.p>}
                    </div>

                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200 mt-6" data-id="element-605">
                      <span className="font-bold text-slate-700" data-id="element-606">
                        Total Amount
                      </span>
                      <span className="text-xl font-bold text-brand-teal" data-id="element-607">
                        ₹{total.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 pt-6" data-id="element-608">
                  <button onClick={handleGenerateBill} disabled={total <= 0 || !paymentConfirmed} className="flex-1 inline-flex items-center justify-center gap-2 bg-brand-teal text-white px-4 py-3 rounded-xl font-medium text-sm hover:bg-brand-tealHover transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-609">
                    <Receipt className="w-4 h-4" data-id="element-610" />
                    Generate Bill
                  </button>
                  <button onClick={handleReset} className="inline-flex items-center justify-center gap-2 bg-white text-slate-600 px-4 py-3 rounded-xl font-medium text-sm border border-slate-200 hover:bg-slate-50 transition-colors" data-id="element-611">
                    Cancel
                  </button>
                </div>
              </div>
            </motion.div>}

          {/* Bill Generated Success */}
          {billGenerated && searchResult && <motion.div key="confirmed" initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="border border-green-200 bg-green-50 rounded-xl p-8 text-center" data-id="element-612">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            bounce: 0.5,
            delay: 0.1
          }} data-id="element-613">
                <CheckCircle2Icon className="w-14 h-14 text-green-500 mx-auto mb-3" data-id="element-614" />
              </motion.div>
              <h3 className="text-lg font-bold text-green-800 mb-1" data-id="element-615">
                Bill Generated Successfully
              </h3>
              <p className="text-sm text-green-600 mb-1" data-id="element-616">
                Invoice No:{' '}
                <span className="font-mono font-bold" data-id="element-617">{billNumber}</span>
              </p>
              <div className="flex items-center justify-center gap-4 text-xs text-green-600 mb-4 font-medium" data-id="element-618">
                <span data-id="element-619">Total: ₹{total.toFixed(2)}</span>
                <span className="w-1 h-1 rounded-full bg-green-400" data-id="element-620" />
                <span data-id="element-621">Paid via {paymentMethod}</span>
              </div>
              <div className="flex items-center justify-center gap-3 mt-2" data-id="element-622">
                <button onClick={() => setShowReceipt(true)} className="inline-flex items-center gap-2 bg-white text-green-700 px-5 py-2.5 rounded-xl font-medium text-sm border border-green-300 hover:bg-green-100 transition-colors" data-id="element-623">
                  <PrinterIcon className="w-4 h-4" data-id="element-624" />
                  Print Bill
                </button>
                <button onClick={handleReset} className="inline-flex items-center gap-2 bg-green-600 text-white px-5 py-2.5 rounded-xl font-medium text-sm hover:bg-green-700 transition-colors" data-id="element-625">
                  Create Another Bill
                </button>
              </div>
            </motion.div>}

          {/* Empty State */}
          {!hasSearched && <motion.div key="empty" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center bg-slate-50" data-id="element-626">
              <SearchIcon className="w-10 h-10 text-slate-300 mx-auto mb-3" data-id="element-627" />
              <p className="text-slate-500 text-sm font-medium" data-id="element-628">
                Search for a gate pass to create bill
              </p>
              <p className="text-slate-400 text-xs mt-1" data-id="element-629">
                Enter a gate pass number or vehicle number above
              </p>
            </motion.div>}
        </AnimatePresence>
      </motion.div>

      {/* Payment Modal */}
      <AnimatePresence data-id="element-630">
        {showPaymentModal && <PaymentModal type={showPaymentModal} amount={total} onConfirm={handlePaymentConfirmed} onClose={() => setShowPaymentModal(null)} data-id="element-631" />}
      </AnimatePresence>

      {/* Bill Receipt Print Modal */}
      {showReceipt && searchResult && <BillReceipt billNo={billNumber} gatePassNo={searchResult.gatePassNo} vehicleNo={searchResult.vehicleNo} amount={total} paymentMode={paymentMethod} dateTime={new Date().toLocaleString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    })} onClose={() => setShowReceipt(false)} data-id="element-632" />}
    </div>;
}